<template>
  <div class="merchant-form-container">
    <div class="form-wrapper">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <h1 class="page-title">{{ isEditMode ? 'Edit Merchant' : 'Register New Merchant' }}</h1>
            <p class="page-subtitle">
              {{ isEditMode ? 'Update merchant profile and branch association for' : 'Onboard a new merchant entity within' }}
              <strong class="text-dark">{{ currentTenantName }}</strong>
            </p>
          </div>
          <div class="header-actions">
            <button type="button" class="btn-secondary" @click="handleCancel">
              <i class="fas fa-arrow-left me-2"></i>Back to List
            </button>
            <button type="button" class="btn-primary" @click="handleSubmit" :disabled="isLoading">
              <span v-if="isLoading"><i class="fas fa-circle-notch fa-spin me-2"></i>Saving...</span>
              <span v-else><i :class="isEditMode ? 'fas fa-save' : 'fas fa-plus-circle'" class="me-2"></i>{{ isEditMode ? 'Update' : 'Create' }} Merchant</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Error Message Banner -->
      <div v-if="errorMessage" class="error-banner">
        <div class="banner-icon">
          <i class="fas fa-exclamation-circle"></i>
        </div>
        <div class="banner-text">{{ errorMessage }}</div>
        <button class="banner-close" @click="errorMessage = ''">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Main Form Grid -->
      <div class="form-grid-layout">
        <!-- Left: Primary Details -->
        <div class="layout-main">
          <div class="form-section-card">
            <!-- Merchant Identity -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-tag"></i>
                <div class="header-info">
                  <h3>Merchant Identity</h3>
                  <p>Core business identifiers and branch routing</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>Merchant Code <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="merchant.code" 
                    class="form-input" 
                    placeholder="e.g. MER-001"
                    :disabled="isEditMode"
                  >
                  <span v-if="isEditMode" class="field-hint">Code cannot be changed once established</span>
                  <div v-if="validation.code" class="error-msg">{{ validation.code[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Merchant Name <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="merchant.name" 
                    class="form-input" 
                    placeholder="e.g. Acme Retail Express"
                  >
                  <div v-if="validation.name" class="error-msg">{{ validation.name[0] }}</div>
                </div>

                <div class="form-field-group full">
                  <label>Origin Branch <span class="required">*</span></label>
                  <AsyncBranchSelect
                    v-model="merchant.branch_id"
                    :corporate-id="corporateId"
                    placeholder="Select routing branch for this merchant"
                  />
                  <span class="field-hint text-muted">Merchant must be associated with a physical branch for resource grouping</span>
                  <div v-if="validation.branch_id" class="error-msg">{{ validation.branch_id[0] }}</div>
                </div>
              </div>
            </div>

            <!-- Location Details -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-map-marker-alt"></i>
                <div class="header-info">
                  <h3>Location Information</h3>
                  <p>Operating address for this specific merchant entity</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>City/Area <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="merchant.city" 
                    class="form-input" 
                    placeholder="e.g. Jakarta Pusat"
                    @input="merchant.city = $event.target.value.replace(/[^a-zA-Z\s\-.]/g, '')"
                  >
                  <div v-if="validation.city" class="error-msg">{{ validation.city[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>State/Province <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="merchant.state" 
                    class="form-input" 
                    placeholder="e.g. DKI Jakarta"
                    @input="merchant.state = $event.target.value.replace(/[^a-zA-Z\s\-.]/g, '')"
                  >
                  <div v-if="validation.state" class="error-msg">{{ validation.state[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Postcode <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="merchant.postcode" 
                    class="form-input" 
                    placeholder="e.g. 10110"
                    @input="merchant.postcode = $event.target.value.replace(/[^0-9]/g, '')"
                  >
                  <div v-if="validation.postcode" class="error-msg">{{ validation.postcode[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Country <span class="required">*</span></label>
                  <o-select 
                    v-model="merchant.country" 
                    :options="countryOptions"
                    placeholder="Select Country"
                  />
                  <div v-if="validation.country" class="error-msg">{{ validation.country[0] }}</div>
                </div>

                <div class="form-field-group full">
                  <label>Full Business Address</label>
                  <textarea 
                    v-model="merchant.address" 
                    class="form-textarea" 
                    rows="3" 
                    placeholder="Detailed address with block/unit number..."
                  ></textarea>
                  <div v-if="validation.address" class="error-msg">{{ validation.address[0] }}</div>
                </div>
              </div>
            </div>

            <!-- Contact Information -->
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-headset"></i>
                <div class="header-info">
                  <h3>Contact Channels</h3>
                  <p>Operations and support contact points</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>Phone Number <span class="required">*</span></label>
                  <div class="field-prefix-group">
                    <span class="prefix-text" v-if="!unknownCountry">{{ countryCode.trim() }}</span>
                    <input 
                      type="text" 
                      v-model="merchant.phone" 
                      class="form-input" 
                      placeholder="811xxx"
                      @input="merchant.phone = $event.target.value.replace(/[^0-9+\-\s()]/g, '')"
                    >
                  </div>
                  <div v-if="validation.phone" class="error-msg">{{ validation.phone[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Fax Number</label>
                  <input 
                    type="text" 
                    v-model="merchant.fax" 
                    class="form-input" 
                    placeholder="+62 21..."
                    @input="merchant.fax = $event.target.value.replace(/[^0-9+\-\s()]/g, '')"
                  >
                </div>

                <div class="form-field-group">
                  <label>Corporate URL</label>
                  <input 
                    type="text" 
                    v-model="merchant.website" 
                    class="form-input" 
                    placeholder="https://..."
                  >
                </div>

                <div class="form-field-group">
                  <label>Commerce / Shop Link</label>
                  <input 
                    type="text" 
                    v-model="merchant.commerce_site" 
                    class="form-input" 
                    placeholder="https://shop..."
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Logo & Metadata Sidebar -->
        <div class="layout-side">
          <div class="form-section-card sticky-box">
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-camera"></i>
                <div class="header-info">
                  <h3>Merchant Logo</h3>
                  <p>Brand visual identity</p>
                </div>
              </div>

              <div class="asset-upload-area">
                <div class="logo-preview-container">
                  <o-image-input 
                    name="logo" 
                    id="logo" 
                    :imageurl="logoPreview" 
                    @selectImage="handleLogoUpload"
                  />
                </div>
                <div class="asset-info">
                  <span class="asset-title">Identity Logo</span>
                  <p>White background or transparent WEBP recommended. (Max 5MB)</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isEditMode" class="form-section-card mt-4">
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-history"></i>
                <div class="header-info">
                  <h3>Metadata</h3>
                  <p>System tracking info</p>
                </div>
              </div>
              <div class="status-summary">
                <div class="summary-item">
                  <span class="label">Merchant ID</span>
                  <span class="value text-monospace">{{ merchant.id }}</span>
                </div>
                <div class="summary-item">
                  <span class="label">Routing Branch</span>
                  <span class="value">{{ merchant.branch_id || 'Not Assigned' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Loading Overlay -->
    <div v-if="isLoading" class="form-loader">
      <div class="loader-content">
        <div class="spinner-border text-danger" role="status"></div>
        <p>Synchronizing merchant records...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import store from '@/store'
import { OSelect, OImageInput } from '@/components/OmnihControl'
import AsyncBranchSelect from '@/components/AsyncBranchSelect.vue'
import { isSupportedCountry, getCountryCallingCode } from 'libphonenumber-js'
import { getCode } from 'country-list'
import { useToast } from '@/composables/useToast'

export default {
  name: 'ExtranetMerchantForm',

  components: {
    OSelect,
    OImageInput,
    AsyncBranchSelect
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const { showToast } = useToast()

    const isLoading = ref(false)
    const validation = ref({})
    const errorMessage = ref('')
    const logoPreview = ref('')
    const corporateId = ref('')

    const merchant = reactive({
      id: '',
      branch_id: '',
      code: '',
      name: '',
      logo: '',
      address: '',
      state: '',
      country: 0,
      phone: '',
      fax: '',
      website: '',
      commerce_site: '',
      city: '',
      postcode: ''
    })

    const countryOptions = ref([])
    const countries = ref([])
    const countryCode = ref('')
    const unknownCountry = ref(false)

    const isEditMode = computed(() => !!route.params.id)
    const currentTenantName = computed(() => store.state.extranet.selectedTenant?.name || '-')

    onMounted(async () => {
      isLoading.value = true
      await loadCountries()
      await loadCorporateId()
      if (isEditMode.value) {
        await loadState(route.params.id)
      }
      isLoading.value = false
    })

    async function loadCountries() {
      try {
        const response = await store.dispatch('Countries/read')
        countries.value = response
        countryOptions.value = response.map(country => ({ value: country.id, text: country.name }))
      } catch (err) {
        errorMessage.value = 'Failed to load geopolitical master data.'
      }
    }

    async function loadCorporateId() {
      try {
        const bp = await store.dispatch('Auth/getBusinessProfile')
        corporateId.value = bp.corporate_id
      } catch (err) {
        errorMessage.value = 'Failed to resolve corporate context.'
      }
    }

    async function loadState(id) {
      try {
        const data = await store.dispatch('ExtranetMerchant/read_single', id)
        merchant.id = data.id
        merchant.code = data.code
        merchant.name = data.name
        merchant.branch_id = data.branch_id
        merchant.country = data.business_profile.country
        merchant.address = data.business_profile.address
        merchant.state = data.business_profile.state
        merchant.city = data.business_profile.city
        merchant.postcode = data.business_profile.postcode
        merchant.fax = data.business_profile.fax
        merchant.website = data.business_profile.website
        merchant.commerce_site = data.business_profile.commerce_site
        
        // Phone parsing
        merchant.phone = removeCountryCode(data.business_profile.phone, data.business_profile.country)
        
        if (data.business_profile.logo?.image_url) {
          logoPreview.value = data.business_profile.logo.image_url
        }
      } catch (err) {
        showToast({ type: 'error', title: 'Data Retrieval Error', message: 'Could not fetch merchant information.' })
        router.push({ name: 'ExtranetMerchants' })
      }
    }

    function removeCountryCode(phone, countryId) {
      if (!countries.value.length) return phone
      const country = countries.value.find(c => c.id === parseInt(countryId))
      if (country) {
        const isoCode = getCode(country.name)
        if (isSupportedCountry(isoCode)) {
          const callingCode = getCountryCallingCode(isoCode)
          const pattern = new RegExp(`^\\+${callingCode}`)
          return phone.replace(pattern, '').trim()
        }
      }
      return phone
    }

    function updatePhoneCode() {
      if (!countries.value.length || !merchant.country) return
      const country = countries.value.find(c => c.id === parseInt(merchant.country))
      if (country) {
        const isoCode = getCode(country.name)
        if (isSupportedCountry(isoCode)) {
          unknownCountry.value = false
          countryCode.value = `+${getCountryCallingCode(isoCode)} `
        } else {
          unknownCountry.value = true
        }
      } else {
        unknownCountry.value = true
      }
    }

    function handleLogoUpload(img) {
      merchant.logo = img
      logoPreview.value = img
    }

    function validateForm() {
      validation.value = {}
      let isValid = true
      const required = ['name', 'branch_id', 'city', 'state', 'postcode', 'country', 'phone']
      if (!isEditMode.value) required.push('code')

      required.forEach(field => {
        if (!merchant[field]) {
          validation.value[field] = [`${field.replace('_', ' ')} is required`]
          isValid = false
        }
      })
      return isValid
    }

    async function handleSubmit() {
      if (!validateForm()) {
        errorMessage.value = 'Required fields are missing or invalid.'
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      isLoading.value = true
      const payload = { ...merchant }
      payload.phone = countryCode.value + payload.phone

      try {
        if (isEditMode.value) {
          if (payload.logo === '') delete payload.logo
          await store.dispatch('ExtranetMerchant/update', { data: payload })
          showToast({ type: 'success', title: 'Merchant Updated', message: 'Merchant records successfully synchronized.' })
        } else {
          await store.dispatch('ExtranetMerchant/create', { data: payload })
          showToast({ type: 'success', title: 'Merchant Registered', message: 'New merchant entity created.' })
        }
        router.push({ name: 'ExtranetMerchants' })
      } catch (err) {
        if (err.response?.data?.error) {
          try {
            validation.value = JSON.parse(err.response.data.error)
          } catch (e) {
            errorMessage.value = err.response.data.message || 'API Validation error'
          }
        } else {
          errorMessage.value = err.response?.data?.message || 'Server synchronization failed.'
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } finally {
        isLoading.value = false
      }
    }

    function handleCancel() {
      router.push({ name: 'ExtranetMerchants' })
    }

    watch(() => merchant.country, updatePhoneCode)

    return {
      merchant, isLoading, validation, errorMessage, logoPreview,
      countryOptions, isEditMode, currentTenantName, countryCode, unknownCountry,
      corporateId, handleLogoUpload, handleSubmit, handleCancel
    }
  }
}
</script>

<style scoped>
.merchant-form-container {
  min-height: 100vh;
}

.form-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Banners */
.error-banner {
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: var(--extranet-rounded);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
}

.banner-icon { color: #ef4444; font-size: 18px; }
.banner-text { flex: 1; color: #b91c1c; font-size: 14px; font-weight: 500; }
.banner-close { background: transparent; border: none; color: #991b1b; cursor: pointer; opacity: 0.6; }

/* Grid Layout */
.form-grid-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;
  align-items: start;
}

.form-section-card {
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid var(--extranet-accent);
  padding: 40px;
}

.sticky-box { position: sticky; top: 32px; }

/* Blocks */
.content-block {
  padding-bottom: 40px;
  margin-bottom: 40px;
  border-bottom: 1px solid #f1f5f9;
}

.content-block.no-border { padding-bottom: 0; margin-bottom: 0; border-bottom: none; }

.block-header { display: flex; gap: 16px; margin-bottom: 24px; }
.block-header i {
  width: 42px; height: 42px;
  background: #fff5f5; color: var(--extranet-primary);
  border-radius: var(--extranet-rounded);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}

.header-info h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
.header-info p { font-size: 13px; color: #64748b; margin: 0; }

/* Inputs */
.fields-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
.form-field-group { display: flex; flex-direction: column; gap: 8px; }
.form-field-group.full { grid-column: 1 / -1; }
.form-field-group label { font-size: 13px; font-weight: 700; color: #475569; }
.required { color: var(--extranet-primary); }

.form-input, .form-textarea {
  padding: 12px 16px;
  border: 1px solid var(--extranet-accent);
  border-radius: var(--extranet-rounded);
  font-size: 14px; color: #1e293b; transition: all 0.2s; width: 100%;
}

.form-input:focus, .form-textarea:focus {
  outline: none; border-color: var(--extranet-primary);
  box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.05);
}

.form-input:disabled { background: #f8fafc; cursor: not-allowed; color: #94a3b8; }
.field-hint { font-size: 12px; color: #94a3b8; }
.error-msg { font-size: 12px; color: #ef4444; font-weight: 500; }

/* Prefix Group */
.field-prefix-group { display: flex; align-items: stretch; }
.field-prefix-group .form-input { border-top-left-radius: 0; border-bottom-left-radius: 0; }
.prefix-text {
  padding: 12px 16px; background: #f1f5f9;
  border: 1px solid var(--extranet-accent); border-right: none;
  border-top-left-radius: var(--extranet-rounded);
  border-bottom-left-radius: var(--extranet-rounded);
  color: #64748b; font-size: 14px; font-weight: 600;
  display: flex; align-items: center;
}

/* Asset Sidebar */
.asset-upload-area {
  padding: 24px;
  background: #f8fafc;
  border-radius: var(--extranet-rounded);
  text-align: center;
}

.logo-preview-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

:deep(.o-image-input),
:deep(.o-image-input .image-preview),
:deep(.o-image-input .upload-placeholder) {
  border-radius: var(--extranet-rounded) !important;
}

.asset-info { text-align: center; }
.asset-title { display: block; font-size: 14px; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
.asset-info p { font-size: 12px; color: #94a3b8; margin: 0; line-height: 1.6; }

/* Status Summary */
.status-summary { display: flex; flex-direction: column; gap: 12px; }
.summary-item { display: flex; justify-content: space-between; align-items: center; }
.summary-item .label { font-size: 13px; color: #64748b; font-weight: 500; }
.summary-item .value { font-size: 13px; color: #1e293b; font-weight: 700; }
.text-monospace { font-family: 'JetBrains Mono', monospace; font-size: 12px; }

/* Loader */
.form-loader {
  position: fixed; inset: 0; background: rgba(255,255,255,0.7);
  backdrop-filter: blur(4px); display: flex; align-items: center;
  justify-content: center; z-index: 1000;
}

.loader-content { text-align: center; }
.loader-content p { margin-top: 16px; font-weight: 600; color: #475569; }

/* Common UI */
.btn-primary, .btn-secondary {
  padding: 10px 24px; border-radius: var(--extranet-rounded);
  font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s;
  display: inline-flex; align-items: center; border: none;
}

.btn-primary {
  background: var(--extranet-primary); color: white;
  box-shadow: 0 4px 12px rgba(181, 26, 31, 0.15);
}

.btn-primary:hover:not(:disabled) { filter: brightness(1.1); transform: translateY(-1px); }

.btn-secondary {
  background: white; color: #475569; border: 1px solid var(--extranet-accent);
}

.btn-secondary:hover { background: #f8fafc; color: #1e293b; }

.text-dark { color: #1e293b; }

@media (max-width: 1024px) {
  .form-grid-layout { grid-template-columns: 1fr; }
  .sticky-box { position: static; }
}
</style>
